import { Injectable, Injector } from '@angular/core';
import { BaseService } from './base.service';
import { DayLightSavingRule } from '../models/day-light-saving-rule.model';
import { ZipCodeInfo } from '../models/zipcode-info';
import { parseISO } from 'date-fns/parseISO';
import { addDays } from 'date-fns/addDays';
import { addHours } from 'date-fns/addHours';
import { getYear } from 'date-fns/getYear';
import { getDay } from 'date-fns/getDay';
import { isWithinInterval } from 'date-fns/isWithinInterval';
import { startOfMonth } from 'date-fns/startOfMonth';
import { DayOfWeek } from '../enums/dayOfWeek';

@Injectable({
  providedIn: 'root'
})
export class UtcConversionsService extends BaseService {

  className = "UtcConversionsService";

  // NOTE: all US timezones have the same daylight saving rule.
  //  - Begin on the 2nd Sunday of the 3rd month at 2:00 AM.
  //  - End on the 1st Sunday of the 11th month at 2:00 AM.
  public readonly dayLightSavingStart = new DayLightSavingRule("03", "02");
  public readonly dayLightSavingEnd = new DayLightSavingRule("11", "01");
  public localDateTime: Date;
  public utcDateTime: Date;

  constructor(
    injector: Injector) {
    super(injector);
  }

  public async createUTCDateTimeBasedOnZip(zipInformation: ZipCodeInfo, date: string, time: string) {
    const dateTimeString = `${date}T${time}`;
    let scheduledDate = parseISO(dateTimeString);

    let standardOffset = zipInformation.standardUTCOffset;
    if (zipInformation.observesDaylightSavingTime) {
      let dayLightSavingOffset = this.getDayLightHourOffset(scheduledDate);
      standardOffset = standardOffset + dayLightSavingOffset;
    }

    let standardUTCOffsetString = this.getStandardOffsetString(standardOffset);
    let scheduledDateTimeWithoutOffset = dateTimeString;

    const offsetDateTimeString = `${scheduledDateTimeWithoutOffset}-${standardUTCOffsetString}:00`;
    let scheduledDateWithUTCOffset = parseISO(offsetDateTimeString);

    return scheduledDateWithUTCOffset.toISOString();
  }

  public async createLocalDateTimeBasedOnZip(zipInformation: ZipCodeInfo, dateTime: string) {
    let standardOffset = zipInformation.standardUTCOffset;
    
    // Parse the dateTime with timezone offset
    const offsetDateTimeString = `${dateTime}+${this.getStandardOffsetString(standardOffset)}:00`;
    let scheduledDate = parseISO(offsetDateTimeString);

    let dayLightSavingOffset = 0;
    if (zipInformation.observesDaylightSavingTime) {
      dayLightSavingOffset = this.getDayLightHourOffset(scheduledDate);
      scheduledDate = addHours(scheduledDate, dayLightSavingOffset);
    }

    return scheduledDate.toISOString();
  }

  private getDayLightHourOffset(dateTime: Date): number {
    var daylightStartDate = this.getDaylightSavingDateTime(getYear(dateTime), this.dayLightSavingStart);
    var daylightEndDate = this.getDaylightSavingDateTime(getYear(dateTime), this.dayLightSavingEnd);
    return isWithinInterval(dateTime, { start: daylightStartDate, end: daylightEndDate }) ? 1 : 0;
  }

  private getDaylightSavingDateTime(year: number, rule: DayLightSavingRule): Date {
    // start at the 1st day of the month at 2:00 AM, and then we'll find the Nth occurrence of Sunday.
    var value = startOfMonth(new Date(year, parseInt(rule.month) - 1, 1));
    var dayDiff = DayOfWeek.Sunday - getDay(value);
    if (dayDiff < 0) {
      dayDiff += 7;
    }
    dayDiff += 7 * (Number(rule.nthDayOfWeek) - 1);
    if (dayDiff > 0) {
      value = addDays(value, dayDiff);
    }

    // Set time to 2:00 AM
    const utcDate = value.toISOString();
    const utcDateWithoutOffset = utcDate.substring(0, utcDate.indexOf('T'));
    return parseISO(`${utcDateWithoutOffset}T02:00:00`);
  }

  private getStandardOffsetString(standardUTCOffset: number): string {
    let standardUTCOffsetString = standardUTCOffset < -9
      ? (-1 * standardUTCOffset).toString()
      : `0${(-1 * standardUTCOffset)}`;

    return standardUTCOffsetString;
  }
}
